import { useParams, useLocation } from 'react-router-dom'
import { useState, useEffect, useContext } from "react"
import { useSelector } from 'react-redux'
import { APIContext } from 'context/APIContext'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { Switch } from '@headlessui/react'
import moment from "moment"

import ScreenFrame from "components/Frame/ScreenFrame"
import KinesisProfileBreadCrumbs from 'components/Profile/KinesisProfileBreadCrumbs'
import ContentFrame from "components/Frame/ContentFrame"
import BrandColorButton from "components/Buttons/BrandColorButton"

export default function InjuriesView() {

    const { urlAthleteId } = useParams()
    const location = useLocation()

    const parsedUrlAthleteId = urlAthleteId != null ? parseInt(urlAthleteId) : null

    const { RegenerateActiveProgramIncompleteWorkouts } = useContext(APIContext)

    const simulationClockDayOffset = useSelector((state) => state.simulationClock.simulationClockDayOffset)
    const clientDate = moment().local().add(simulationClockDayOffset, 'days').format('YYYY-MM-DD')

    const [injurtData, setInjurtData] = useState(null)
    const [selectedInjury, setSelectedInjury] = useState(null)
    const [isSaving, setIsSaving] = useState(false)

    const GetAthleteInjuryDataFromBackend = async () => {
        const jsonBody = {
            userId: parsedUrlAthleteId
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('PROGRAM', 'GET', '/trainee_injury', jsonBody)

        // console.log(result)

        if (result.data.status === 'failure') {
            console.error('Failed to fetch injury data:', result.data.message)
            return
        }

        setInjurtData(result.data.data)
        setSelectedInjury(result.data.data.activeInjuries.length > 0 ? result.data.data.activeInjuries[0] : null)
    }

    const HandleInjuryToggle = (injuryType) => {

        var temp_injury_data = selectedInjury === injuryType ? null : injuryType

        // console.log(temp_injury_data)

        setSelectedInjury(temp_injury_data)

    }

    const SaveChanges = async () => {
        try {
            setIsSaving(true)

            const jsonBody = {
                userId: parsedUrlAthleteId,
                activeInjuries: selectedInjury ? [selectedInjury] : [],
            }

            // console.log(jsonBody)

            const result = await WhichWeightAPI('PROGRAM', 'POST', '/trainee_injury_update', jsonBody)

            // console.log(result)

            if (result.data.status !== 'success') {
                console.error('Failed to update injuries:', result.data.message)
                return
            }

            await RegenerateActiveProgramIncompleteWorkouts(null, parsedUrlAthleteId, clientDate)

            window.location.reload()
        } catch (error) {
            console.error('Error updating injuries:', error)
        } finally {
            setIsSaving(false)
        }
    }

    useEffect(() => { GetAthleteInjuryDataFromBackend() }, [])

    if (injurtData === null) {
        return (
            <ScreenFrame>
                <div className='max-w-2xl mx-auto space-y-4'>
                    <div className='flex-shrink-0 h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='flex-shrink-0 h-[400px] bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame>
            <div className='max-w-2xl mx-auto space-y-4'>
                <KinesisProfileBreadCrumbs 
                    propProfileType='individual' 
                    profileName={location.state?.profileName} 
                    currentPageName='Injuries' 
                />

                <div className='flex items-center space-y-1 gap-x-2 pb-1'>
                    <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex-grow">
                        Injuries
                    </h1>

                    <div className="w-32">
                        <BrandColorButton
                            size="extra-small"
                            text="Save Changes"
                            action={SaveChanges}
                            isLoading={isSaving}
                            className="shadow-md"
                        />
                    </div>
                </div>

                <ContentFrame>
                    <div className='w-full p-4 space-y-4'>
                        <div className="flex items-center justify-between">
                            <span className="text-gray-700">Patellar Tendon</span>

                            <Switch
                                checked={selectedInjury === 'patellar-tendon'}
                                onChange={() => HandleInjuryToggle('patellar-tendon')}
                                className={`
                                relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2
                                ${selectedInjury === 'patellar-tendon' ? 'bg-orange-400' : 'bg-gray-200'}
                            `}
                            >
                                <span className="sr-only">Patellar Tendon Injury</span>
                                <span
                                    aria-hidden="true"
                                    className={`
                                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                                    ${selectedInjury === 'patellar-tendon' ? 'translate-x-5' : 'translate-x-0'}
                                `}
                                />
                            </Switch>
                        </div>

                        <div className="flex items-center justify-between">
                            <span className="text-gray-700">Achilles Tendon</span>

                            <Switch
                                checked={selectedInjury === 'achilles-tendon'}
                                onChange={() => HandleInjuryToggle('achilles-tendon')}
                                className={`
                                relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2
                                ${selectedInjury === 'achilles-tendon' ? 'bg-orange-400' : 'bg-gray-200'}
                            `}
                            >
                                <span className="sr-only">Achilles Tendon Injury</span>
                                <span
                                    aria-hidden="true"
                                    className={`
                                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                                    ${selectedInjury === 'achilles-tendon' ? 'translate-x-5' : 'translate-x-0'}
                                `}
                                />
                            </Switch>
                        </div>

                        <div className="flex items-center justify-between">
                            <span className="text-gray-700">Bone Stress Injury</span>

                            <Switch
                                checked={selectedInjury === 'bone-stress'}
                                onChange={() => HandleInjuryToggle('bone-stress')}
                                className={`
                                relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2
                                ${selectedInjury === 'bone-stress' ? 'bg-orange-400' : 'bg-gray-200'}
                            `}
                            >
                                <span className="sr-only">Bone Stress Injury</span>
                                <span
                                    aria-hidden="true"
                                    className={`
                                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                                    ${selectedInjury === 'bone-stress' ? 'translate-x-5' : 'translate-x-0'}
                                `}
                                />
                            </Switch>
                        </div>
                    </div>
                </ContentFrame>

                {injurtData.injuryHistory.length > 0 && (
                    <>
                        <div>Injury History</div>

                        <ContentFrame>
                            <table className="w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Injury Type</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {injurtData.injuryHistory.map((injury, index) => (
                                        <tr key={index}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="font-medium capitalize">{injury.injuryType.replace('-', ' ')}</span>
                                            </td>

                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {moment.utc(injury.createdTimestamp).local().format('MMMM DD, YYYY')}
                                            </td>

                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {injury.endTimestamp && injury.endTimestamp !== ''
                                                    ? moment.utc(injury.endTimestamp).local().format('MMMM DD, YYYY')
                                                    : <span className="text-gray-400 italic">Ongoing</span>
                                                }
                                            </td>

                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${injury.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {injury.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </ContentFrame>
                    </>
                )}
            </div>
        </ScreenFrame>
    )
} 